/**
 * Shoppable video carousel (sections/shoppable-video.liquid).
 *
 * Hard performance rules this file enforces:
 *  - Every <video> ships preload="none" (set in the Liquid); this module
 *    never overrides that.
 *  - A tile's <source src> is only populated (armed) once an
 *    IntersectionObserver reports it near the viewport — never on page load,
 *    never on a scroll listener.
 *  - At most one video plays at a time, full stop. Desktop (fine pointer +
 *    hover): the hovered/focused tile plays. Touch (no hover): the tile
 *    centered in the track plays, tracked via a second IntersectionObserver
 *    scoped to a thin central band of the track — still no scroll listener.
 *  - Constrained visitors (prefers-reduced-motion, Save-Data, or an
 *    effective connection of slow-2g/2g/3g) never get inline autoplay at
 *    all: tiles stay poster-only and are not even armed. Their "tap to
 *    play" moment is the modal, which is the only place video ever loads
 *    for them, on the explicit tap that opens it.
 *  - Opening the modal pauses whatever tile video is currently playing, so
 *    the one-video invariant holds across the whole carousel + modal.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const conn = navigator.connection;

  // Indian mobile connections regularly report "3g" under real-world load;
  // treated as constrained alongside 2g/slow-2g and explicit data-saver.
  const constrained = () =>
    reducedMotion.matches ||
    conn?.saveData === true ||
    /(^|-)2g$|^3g$/.test(conn?.effectiveType || '');

  class ShoppableVideoCarousel extends HTMLElement {
    /** @type {HTMLVideoElement | null} */
    #activeVideo = null;

    connectedCallback() {
      this.track = this.querySelector('[data-sv-track]');
      this.modal = this.querySelector('[data-sv-modal]');
      if (!this.track) return;

      this.#setupVideos();
      this.#setupArrows();
      this.#setupModal();
    }

    disconnectedCallback() {
      this.armObserver?.disconnect();
      this.centerObserver?.disconnect();
    }

    /* ---- lazy load + single-active-video playback ---- */
    #setupVideos() {
      const tiles = [...this.track.querySelectorAll('.video-tile')];
      if (!tiles.length) return;

      reducedMotion.addEventListener?.('change', () => {
        if (reducedMotion.matches && this.#activeVideo) this.#pause(this.#activeVideo);
      });

      if (constrained()) return; // poster-only: nothing armed, nothing plays

      this.armObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const video = entry.target.querySelector('.video-tile__video');
            if (video) this.#arm(video);
            this.armObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '150px' }
      );
      tiles.forEach((tile) => this.armObserver.observe(tile));

      if (desktopPointer.matches) {
        // Desktop: hovered/focused tile plays; leaving pauses it.
        tiles.forEach((tile) => {
          const video = tile.querySelector('.video-tile__video');
          if (!video) return;
          tile.addEventListener('pointerenter', () => this.#playOnly(video));
          tile.addEventListener('pointerleave', () => this.#pause(video));
          tile.addEventListener('focusin', () => this.#playOnly(video));
          tile.addEventListener('focusout', () => this.#pause(video));
        });
        return;
      }

      // Touch: the tile centered in the track's viewport plays. A thin
      // central band (root shrunk 40% from each side) stands in for "which
      // card is centered" — no scroll listener, IntersectionObserver only.
      this.centerObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = entry.target.querySelector('.video-tile__video');
            if (!video) continue;
            if (entry.isIntersecting) {
              this.#playOnly(video);
            } else if (video === this.#activeVideo) {
              this.#pause(video);
            }
          }
        },
        { root: this.track, rootMargin: '0px -40% 0px -40%', threshold: 0 }
      );
      tiles.forEach((tile) => this.centerObserver.observe(tile));
    }

    #arm(video) {
      const source = video.querySelector('source[data-src]');
      if (source && !source.src) {
        source.src = source.dataset.src;
        video.load();
      }
    }

    #playOnly(video) {
      if (this.#activeVideo && this.#activeVideo !== video) this.#pause(this.#activeVideo);
      this.#arm(video);
      video
        .play()
        .then(() => video.classList.add('is-playing'))
        .catch(() => {});
      this.#activeVideo = video;
    }

    #pause(video) {
      if (!video.paused) video.pause();
      video.classList.remove('is-playing');
      if (this.#activeVideo === video) this.#activeVideo = null;
    }

    /* ---- desktop arrows ---- */
    #setupArrows() {
      const prev = this.querySelector('[data-sv-prev]');
      const next = this.querySelector('[data-sv-next]');
      if (!prev || !next) return;

      const step = () => {
        const tile = this.track.querySelector('.video-tile');
        if (!tile) return this.track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
        return tile.getBoundingClientRect().width + gap;
      };

      prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: reducedMotion.matches ? 'instant' : 'smooth' }));
      next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: reducedMotion.matches ? 'instant' : 'smooth' }));

      let raf = null;
      const sync = () => {
        raf = null;
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        prev.disabled = this.track.scrollLeft <= 2;
        next.disabled = this.track.scrollLeft >= max;
      };
      this.track.addEventListener('scroll', () => {
        if (!raf) raf = requestAnimationFrame(sync);
      }, { passive: true });
      sync();
    }

    /* ---- modal: the tap-to-play moment for constrained/poster-only tiles ---- */
    #setupModal() {
      if (!this.modal || this.dataset.clickBehavior !== 'modal') return;
      const content = this.modal.querySelector('[data-sv-modal-content]');

      this.track.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-video-tile-trigger]');
        if (!trigger) return;
        const template = trigger.querySelector('[data-video-tile-modal]');
        if (!template) return;

        // Enforce the one-video invariant across tile + modal.
        if (this.#activeVideo) this.#pause(this.#activeVideo);

        content.replaceChildren(template.content.cloneNode(true));
        this.modal.showModal();

        const video = content.querySelector('.video-modal__video');
        if (video) {
          video.src = video.dataset.src;
          video.muted = false;
          // click is a user gesture, so unmuted play is usually allowed;
          // fall back to muted playback rather than a frozen frame.
          video.play().catch(() => {
            video.muted = true;
            video.play().catch(() => {});
          });
        }
      });

      const close = () => this.modal.close();
      this.modal.querySelector('[data-sv-modal-close]')?.addEventListener('click', close);
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) close();
      });
      this.modal.addEventListener('close', () => {
        content.querySelector('.video-modal__video')?.pause();
        content.replaceChildren();
      });
    }
  }

  if (!customElements.get('shoppable-video-carousel')) {
    customElements.define('shoppable-video-carousel', ShoppableVideoCarousel);
  }
})();
